import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useMount } from "react-use";
import { arrayOfJobTypes } from "~/schemas/schema.job_types";
import { arrayOfProjects } from "~/schemas/schema.projects";
import { arrayOfUsers } from "~/schemas/schema.users";
import AppService from "~/services/AppService";
import LocalizationService from "~/services/LocalizationService";
import {
  ANGULAR_INIT,
  FETCH_JOB_TYPES_SUCCESS,
  FETCH_PROJECTS_SUCCESS,
  FETCH_USERS_SUCCESS,
} from "~/store/action_types";
import { UnauthorizedError } from "~/utils/errors";

export const useAuth = (onUserAuthenticated) => {
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);
  const [data, setData] = useState();
  const dispatch = useDispatch();

  useMount(() => {
    const handleData = (response, isAuthenticated) => {
      const { settings = {}, languages = [] } = response;
      const {
        login_policy = {},
        password_policy = {},
        theme = "indigo",
        on_demand = false,
        csrf_validator_cookie_name,
      } = settings;

      if (csrf_validator_cookie_name) {
        window.csrfValidatorName = csrf_validator_cookie_name;
      }

      setData({
        loginPolicy: login_policy,
        passwordPolicy: password_policy,
        theme,
        instanceType: on_demand ? "cloud" : "self-hosted",
        isAuthenticated,
        languages,
      });
    };
    AppService.getUserSession()
      .then((response) => {
        handleData(response, true);
        setLoading(false);
      })
      .catch((error) => {
        if (error instanceof UnauthorizedError) {
          const data = error.data;
          const { languages = [], settings = {} } = data;
          const { format_time } = settings;

          handleData(data, false);
          const defaultLanguage = languages.find((l) => l.is_default);
          if (defaultLanguage) {
            LocalizationService.configure(
              {
                language_id: defaultLanguage.id,
                format_time,
              },
              languages,
              true
            );
          }
          setLoading(false);
        }
      });
  });

  const authenticate = useCallback(
    async (data) => {
      const response = await AppService.login(data);
      const {
        users,
        logged_user_id,
        favorites,
        projects,
        feed_token,
        companies,
        job_types,
        system_notifications,
        settings: personalized_config_options,
        instance_id,
      } = response;
      const logged_user = users.find((user) => user.id === logged_user_id);
      const { homepage } = personalized_config_options;
      await Promise.all([
        dispatch({
          type: FETCH_USERS_SUCCESS,
          response: users,
          schema: arrayOfUsers,
        }),
        dispatch({
          type: FETCH_PROJECTS_SUCCESS,
          response: projects,
          schema: arrayOfProjects,
        }),
        dispatch({
          type: FETCH_JOB_TYPES_SUCCESS,
          response: job_types,
          schema: arrayOfJobTypes,
        }),
        dispatch({
          type: ANGULAR_INIT,
          response: {
            logged_user,
            personalized_config_options,
            config_options: {},
            companies,
            instance_id,
            favorites,
            system_notifications,
            homepage,
            feed_token,
          },
        }),
      ]);
      if (typeof onUserAuthenticated === "function") {
        onUserAuthenticated();
      }
    },
    [dispatch, onUserAuthenticated]
  );

  return {
    loading,
    error,
    data,
    authenticate,
  };
};
